import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit
} from '@angular/core';

import {Observable, Subject} from 'rxjs';

import {FormBuilder, FormGroup} from '@angular/forms';
import {removeEmptyFormElements} from '../../../shared/utilities/forms.utility';
import {SearchAddonDataService} from '../../services/search-addon-data.service';
import {CorrectionInvoice, PayableInvoice} from '../../models';
import {Department} from '../../../master-data/models';
import {DepartmentsSelectors} from '../../../master-data/store/selectors';
import {loadIfNotLoaded} from '../../../shared/utilities/observable.utility';
import {DepartmentsActions} from '../../../master-data/store';
import {Store} from '@ngrx/store';
import {ApplicationState} from '../../../application-state/store';
import {LocalStorageService} from "../../../shared/services";

@Component({
  selector: 'app-payable-invoices-filter',
  styleUrls: ['payable-invoices-filter-form.component.scss'],
  template: `
    <div [formGroup]="form" class="form">
      <div class="row">
        <div class="col-9">
          <div class="row">
            <div class="col-2">
              <mat-form-field>
                <input
                  type="text"
                  matInput
                  placeholder="Autragsnummer"
                  formControlName="orderNumber"
                />
              </mat-form-field>
            </div>
            <div class="col-3">
              <ng-select
                placeholder="Bereich"
                [items]="departments$ | async"
                [loading]="departmentIsLoading$ | async"
                bindValue="@id"
                bindLabel="name"
                [markFirst]="false"
                [clearable]="true"
                formControlName="department"
              ></ng-select>
            </div>
            <div class="col-3">
              <ng-select
                placeholder="Status"
                [items]="states"
                bindValue="value"
                bindLabel="label"
                [markFirst]="false"
                [clearable]="true"
                formControlName="state"
              ></ng-select>
            </div>
            <div class="col-2">
              <mat-form-field>
                <mat-label>Summe(Brutto) größer</mat-label>
                <input type="text" matInput formControlName="minGrossPrice" currencyMask
                       [options]="{ prefix: '', suffix: '€', allowNegative: false}">
                <mat-error>
                  <app-form-error
                    fieldName="minGrossPrice"
                    [formGroup]="form"
                  ></app-form-error>
                </mat-error>
              </mat-form-field>

            </div>
            <div class="col-2">
              <mat-form-field>
                <mat-label>{{"invoices.invoices_preview.invoices_filter.exact_sum" | translate}}</mat-label>
                <input type="text" matInput formControlName="maxGrossPrice" currencyMask
                       [options]="{ prefix: '', suffix: '€', allowNegative: false}">
                <mat-error>
                  <app-form-error
                    fieldName="maxGrossPrice"
                    [formGroup]="form"
                  ></app-form-error>
                </mat-error>
              </mat-form-field>
            </div>
          </div>
        </div>

        <div class="col-3">
          <div class="row">
            <div class="col-6">
              <mat-form-field>
                <mat-label>Rechnungsdatum vom</mat-label>
                <input type="date" matInput formControlName="createdAtMin"/>
                <mat-error>
                  <app-form-error
                    fieldName="createdAtMin"
                    [formGroup]="form"
                  ></app-form-error>
                </mat-error>
              </mat-form-field>
            </div>
            <div class="col-6">
              <mat-form-field>
                <mat-label>Rechnungsdatum bis</mat-label>
                <input type="date" matInput formControlName="createdAtMax"/>
                <mat-error>
                  <app-form-error
                    fieldName="createdAtMax"
                    [formGroup]="form"
                  ></app-form-error>
                </mat-error>
              </mat-form-field>
            </div>
          </div>
          <div class="row">
            <div class="col-12 p-4" style="margin-left: auto; display: flex; justify-content: flex-end;">
              <a (click)="resetFilter()" class="link-primary cursor-pointer">Filter zurücksetzen</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class PayableInvoicesFilterFormComponent implements OnInit, OnDestroy {
  @Output() updateFilter: EventEmitter<{
    [key: string]: string;
  }> = new EventEmitter<{ [key: string]: string }>();
  @Input() items: Array<PayableInvoice>;

  departments$: Observable<Array<Department>>;
  departmentIsLoading$: Observable<boolean>;

  form: FormGroup;
  storedFilters?: any;

  onDestroy$: Subject<any> = new Subject<any>();
  states = [
    {label: 'Bezahlt', value: 'paid'},
    {label: 'Inkasso', value: 'debt_collection'},
    {label: 'Überfällig', value: 'overdue'},
    {label: 'Storniert', value: 'canceled'},
    {label: 'Erste Mahnung', value: 'first_reminder'},
    {label: 'Zweite Mahnung', value: 'second_reminder'},
    {label: 'Gebucht', value: 'booked'},
    {label: 'Entwurf', value: 'draft'}
  ];

  constructor(
    private store$: Store<ApplicationState>,
    private fb: FormBuilder,
    public localStorageService: LocalStorageService
  ) {
  }

  ngOnInit(): void {
    this.loadDepartments();
    this.form = this.fb.group({
      orderNumber: this.fb.control(null),
      department: this.fb.control(null),
      state: this.fb.control(null),
      minGrossPrice: this.fb.control(0),
      maxGrossPrice: this.fb.control(0),
      createdAtMin: this.fb.control(null),
      createdAtMax: this.fb.control(null)
    });
    this.form.valueChanges.subscribe(value => {
      this.updateFilter.emit(removeEmptyFormElements(value));
      this.localStorageService.overwriteObjectByKey('filters', 'payable_invoices', value);
    });

    this.storedFilters = this.localStorageService.getObjectByKey('filters', 'payable_invoices');
    if(this.storedFilters) {
      this.updateFilter.emit(this.storedFilters);
      this.form.patchValue(this.storedFilters);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  resetFilter(): void {
    this.form.patchValue({
      orderNumber: null,
      minGrossPrice: 0,
      maxGrossPrice: 0,
      department: null,
      state: null,
      createdAtMin: null,
      createdAtMax: null,
    });
  }

  private loadDepartments(): void {
    this.departments$ = this.store$.select(
      DepartmentsSelectors.selectDepartments
    );
    this.departmentIsLoading$ = this.store$.select(
      DepartmentsSelectors.isLoading
    );
    loadIfNotLoaded(
      this.store$,
      DepartmentsSelectors.isLoaded,
      DepartmentsActions.ReadDepartments()
    );
  }
}
